import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import type {
  SpecGroupCreateBrowserInput,
  SpecGroupDeleteBrowserInput,
  SpecGroupMoveBrowserInput,
  SpecGroupResetBrowserInput,
  SpecGroupStatusBrowserInput,
  SpecGroupUpdateBrowserInput,
} from "@/api/contracts/spec-editor";
import {
  specGroupCreate,
  specGroupDelete,
  specGroupMove,
  specGroupReset,
  specGroupUpdate,
  specGroupUpdateStatus,
} from "@/api/functions/spec-editor";
import {
  editorMutationOptions,
  NO_ROWS,
  patchLines,
  type EditorGuess,
} from "@/hooks/spec-editor-cache";

// The Editor's group verbs (#799): what #805's group card, its menu and its
// three dialogs call. Each runs through #797's harness (`editorMutationOptions`)
// with its own `ERRORS` key, and its result lands through the one cache
// updater — the groups, lines, orderings, cursors, summary and gating the
// server computed in the write's transaction (ADR-0014 decisions 1–6).
//
// **Only the move guesses** (decision 5 as amended): the drag shows its new
// position at once and the harness rolls it back on failure. Add group, Edit
// group / note, Delete and Mark group as wait for the server — their dialogs
// and menu hold until the result lands, and a failure leaves every row as it
// was.
//
// Each hook's variables are the verb's browser input, which is also its cache
// target (`specificationId`, `versionId`, `scope`).

/** Add group — appended to the version. */
export function createSpecGroupMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupCreateBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupCreate({ data }),
    error: "ADD_GROUP",
    structural: true,
  });
}

export function useCreateSpecGroup() {
  return useMutation(createSpecGroupMutationOptions(useQueryClient()));
}

/** Edit group / note — name and note together; `Clear Note` sends `""`. */
export function updateSpecGroupMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupUpdateBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupUpdate({ data }),
    error: "EDIT_GROUP",
  });
}

export function useUpdateSpecGroup() {
  return useMutation(updateSpecGroupMutationOptions(useQueryClient()));
}

/** Delete group — the group, its lines, selections and placements. */
export function deleteSpecGroupMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupDeleteBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupDelete({ data }),
    error: "DELETE_GROUP",
    structural: true,
  });
}

export function useDeleteSpecGroup() {
  return useMutation(deleteSpecGroupMutationOptions(useQueryClient()));
}

/** Mark group as `<status>` — the group's alive lines. */
export function updateSpecGroupStatusMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupStatusBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupUpdateStatus({ data }),
    error: "UPDATE_GROUP_STATUS",
  });
}

export function useUpdateSpecGroupStatus() {
  return useMutation(updateSpecGroupStatusMutationOptions(useQueryClient()));
}

/**
 * The move's guess: the cached groups reordered by the rule the server
 * applies — the moved group immediately after `afterId`, first for `null` —
 * laid on through the updater's own `patchLines`, so each group's held lines
 * follow it and the page cursor, total and `hasMore` stay as they were. A page
 * set that does not hold the group, or the group it lands after, is left as it
 * is. Idempotent: laid over pages that already show the move, it changes
 * nothing.
 */
export const moveSpecGroupGuess: EditorGuess<SpecGroupMoveBrowserInput> = (
  data,
  { groupId, afterId },
) => {
  const last = data.pages[data.pages.length - 1];
  if (!last) return data;
  const order = last.groups.map((group) => group.id);
  if (!order.includes(groupId) || (afterId !== null && !order.includes(afterId))) return data;
  const rest = order.filter((id) => id !== groupId);
  rest.splice(afterId === null ? 0 : rest.indexOf(afterId) + 1, 0, groupId);
  return patchLines(
    data,
    NO_ROWS,
    {
      kind: "base",
      term: null,
      cursor: last.cursor,
      total: last.total,
      hasMore: last.hasMore,
      groupOrder: rest,
      groups: [],
    },
    false,
  );
};

/** Move group — after `afterId`, first for `null`; optimistic. */
export function moveSpecGroupMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupMoveBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupMove({ data }),
    error: "MOVE_GROUP",
    guess: moveSpecGroupGuess,
    structural: true,
  });
}

export function useMoveSpecGroup() {
  return useMutation(moveSpecGroupMutationOptions(useQueryClient()));
}

/**
 * Reset group name and notes to Base / to Core (#1084, a named scope addition)
 * — the two fields from the base group. Not optimistic: the group ⋮'s row
 * spins and holds, and the menu closes on the answer, as the line ⋮'s Resets'.
 */
export function resetSpecGroupMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecGroupResetBrowserInput>(queryClient, {
    mutationFn: (data) => specGroupReset({ data }),
    error: "RESET_GROUP",
  });
}

export function useResetSpecGroup() {
  return useMutation(resetSpecGroupMutationOptions(useQueryClient()));
}
